import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useGlobalState } from '../pages/context/GlobalStateContext';

import { Box, FormField, Link, List, Popover, SpaceBetween, StatusIndicator } from '@cloudscape-design/components';
import Calendar from '@fullcalendar/react';
import resourceTimelinePlugin from '@fullcalendar/react-scheduler/resource-timeline';
import interactionPlugin from '@fullcalendar/react/interaction';
import '@fullcalendar/react/skeleton.css';
import themePlugin from '@fullcalendar/react/themes/classic';
import '@fullcalendar/react/themes/classic/palette.css';
import '@fullcalendar/react/themes/classic/theme.css';

import { mdiAccountMultiple, mdiHumanMaleBoard, mdiInformation, mdiTagHidden } from '@mdi/js';
import Icon from '@mdi/react';
import { addDays, addYears, format, parse } from 'date-fns';
import '../../src/components/css/FullCalendar.css';
import BadgeCustom from '../components/extras/BadgeCustom';
import Booking from '../components/extras/Booking';
import Confirm from '../components/extras/Confirm';
import { fetchWithWatch } from '../hooks/fetchWithWatch';
import { getCookie, getUrlVersion, getUser, setLoading } from '../pages/commons/functions';
import { ItemBooking, ItemEquipment } from '../resources/types';

interface BookingGridProps {
    date?: string;
    idGroupTypes?: number[];
    bookings?: ItemBooking[];
    blockings?: any[];
    showOnlyBookings?: boolean;
    showPrivates?: boolean;
    onDateClick?: (selectedDate: string, equipment: ItemEquipment) => void;
    onResourceClick?: (equipment: ItemEquipment) => void;
    onBookingClick?: (booking: ItemBooking) => void;
    onBookingDelete?: (booking: ItemBooking) => void;
}
const BookingGrid: React.FC<BookingGridProps> = ({
    date = "",
    bookings,
    blockings,
    showOnlyBookings = true,
    idGroupTypes = [],
    onDateClick,
    onBookingDelete,
    showPrivates = false,
    onBookingClick,
    onResourceClick,
}) => {
    const { menu, parameters, idUser, data } = useGlobalState();
    const [booking, setBooking] = useState<ItemBooking>();
    const [info, setInfo] = useState<any>();
    const calendarRef = useRef(null);
    const [equipments, setEquipments] = useState<ItemEquipment[]>([]);
    const formattedDate = date
        ? format(parse(date, "dd-MM-yyyy", new Date()), "yyyy-MM-dd")
        : null;
    useEffect(() => {
        if (formattedDate && calendarRef.current) {
            const calendarApi = calendarRef.current.getApi();
            calendarApi.gotoDate(formattedDate);
        }
    }, [formattedDate]);
    const handleResourceClick = (resource: any) => {
        //console.log('Resource clicked:', resource.extendedProps);
    };
    const isOverlapping = (startA: any, endA: any, startB: any, endB: any) => {
        return startA < endB && endA > startB;
    };

    const handleEquipments = async () => {
        try {
            setLoading(true);
            const response = await fetchWithWatch(`${getUrlVersion()}/routing/v1/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-routing': '/private/global/equipments/list',
                    'JWT': getCookie("JWT"),

                },
                body: JSON.stringify({
                    isBookCapable: 1,
                    isClubOrOwner: 1,
                    idGroupTypes: idGroupTypes,
                    columns: "idEquipment, registration, equipment, isOutOfFlight, jsonOwners, outOfFlightStart, outOfFlightEnd, idGroupType, groupType, thresholdBefore, thresHoldAfter, minBookHour",
                    orderBy: "registration",
                    order: "asc",
                    orderBy2: "equipment",
                    order2: "asc",
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error: ${response.status}`);
            }

            const data = await response.json();
            if (data && data.equipments) {
                setEquipments(data.equipments);
            }

        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }

    };
    useEffect(() => {
        handleEquipments();
    }, []);

    const filterBlockings = useMemo(() => {
        if (!blockings) return [];
        return blockings
            .map((b: any) => ({
                title: b.reason,
                type: "lock",
                classNames: ['bg-transparent'],
                backgroundColor: "transparent",
                borderColor: "transparent",
                resourceId: `eq${b.idEquipment}`,
                start: parse(`${b.startDateTime}`, "dd-MM-yyyy HH:mm", new Date()),
                end: parse(`${b.endDateTime}`, "dd-MM-yyyy HH:mm", new Date()),
            }));
    }, [blockings]);

    const filterBookings = useMemo(() => {
        if (!bookings) return [];
        return bookings;
        return bookings.filter(
            (booking) => booking.idGroupType === parameters.idGroupType
        );
    }, [bookings, parameters.idGroupType]);


    const filteredEquipments = useMemo(() => {
        if (!equipments) return [];

        return equipments
            .filter((eq: ItemEquipment) => {
                // Check bookings condition
                if (showOnlyBookings && !bookings?.some((b: any) => b.idEquipment === eq.idEquipment)) {
                    return false;
                }

                // Check showPrivates condition:
                // If showPrivates is false, only keep equipments with no owners
                if (!showPrivates && eq.jsonOwners.length > 0) {
                    return false;
                }
                if (parameters.idGroupType && eq.idGroupType !== parameters.idGroupType) {
                    return false;
                }

                return true;
            })
            .map((eq: ItemEquipment) => ({
                id: `eq${eq.idEquipment.toString()}`,
                title: eq.equipment !== "" ? eq.equipment : eq.registration,
                groupId: eq.groupType,
                ...eq,
            }));
    }, [equipments, bookings, showOnlyBookings, showPrivates, parameters?.idGroupType]);

    const filteredOOF =
        equipments.filter((eq: any) => eq.isOutOfFlight === 1)
            .map((b: any) => ({
                title: "FDV",
                type: "oof",
                classNames: ['bg-transparent'],
                backgroundColor: "transparent",
                borderColor: "transparent",
                resourceId: `eq${b.idEquipment}`,
                start: b.outOfFlightStart !== "" ? parse(`${b.outOfFlightStart}`, "dd-MM-yyyy", new Date()) : addYears(new Date(), -10),
                end: b.outOfFlightEnd !== "" ? addDays(parse(`${b.outOfFlightEnd}`, "dd-MM-yyyy", new Date()), 1) : addYears(new Date(), 10),
            }));
    //console.log(filteredOOF)
    const filteredBookings =
        bookings?.flatMap((b: any) => {
            // Standard booking event
            const bookingEvent = {
                title: b.displayName,
                type: "booking",
                classNames: ['bg-transparent'],
                backgroundColor: "transparent",
                borderColor: "transparent",
                resourceId: `eq${b.idEquipment}`,
                start: parse(`${b.dateStart} ${b.timeStart}`, "dd-MM-yyyy HH:mm", new Date()),
                end: parse(`${b.dateEnd} ${b.timeEnd}`, "dd-MM-yyyy HH:mm", new Date()),
                booking: b,
            };


            return [bookingEvent];
        }) || [];

    const documentWidth = useDocumentWidth();


    const handleDateClickLocal = (info: any) => {
        const targetDate = new Date(info.date);
        const resourceProps = info?.resource?.extendedProps;
        if (onDateClick) {
            onDateClick(format(targetDate, "dd-MM-yyyy HH:mm"), resourceProps as ItemEquipment);
        }
    };
    const useAvailableEquipmentSlots = (): any => {
        return useMemo(() => {
            const allEvents2 = [...filterBlockings, ...filteredOOF, ...filteredBookings];
            const availableSlots: any[] = [];
            const baseDate = parse(date, "dd-MM-yyyy", new Date());

            // Normalize events into Date objects for comparison
            const normalizedEvents = allEvents2.map(event => ({
                resourceId: event.resourceId,
                start: new Date(event.start),
                end: new Date(event.end)
            }));

            equipments.forEach(equipment => {
                // Find all existing events assigned to this specific equipment item
                const resourceEvents = normalizedEvents.filter(
                    event => event.resourceId === `eq${equipment.idEquipment}`
                );

                // Iterate through each 1-hour slot from 08:00 to 22:00
                for (let hour = 8; hour < 22; hour++) {
                    const slotStart = new Date(baseDate);
                    slotStart.setHours(hour, 0, 0, 0);

                    const slotEnd = new Date(baseDate);
                    slotEnd.setHours(hour + 1, 0, 0, 0);

                    // Check if this slot overlaps with any existing event for this resource
                    const hasConflict = resourceEvents.some(event =>
                        isOverlapping(slotStart, slotEnd, event.start, event.end)
                    );

                    // If no conflict exists, add the 1-hour available event
                    if (!hasConflict) {
                        availableSlots.push({

                            type: "free",
                            classNames: ['bg-transparent'],
                            backgroundColor: "transparent",
                            borderColor: "transparent",
                            resourceId: `eq${equipment.idEquipment}`,
                            start: slotStart,
                            end: slotEnd,
                            title: 'Available Slot',
                            isAvailable: true
                        });
                    }
                }
            });

            return availableSlots;
        }, [equipments, allEvents, date]);
    };

    const bookingSpaces = useAvailableEquipmentSlots()
    //console.log(bookingSpaces)
    const allEvents = [...filterBlockings, ...filteredOOF, ...filteredBookings];
    return (
        <>
            <style>{`
            /* 1. Collapse only the indent spacer to 0 width */
            .hidden-resource-indent {
              width: 0px !important;
              min-width: 0px !important;
              max-width: 0px !important;
              padding: 0 !important;
              margin: 0 !important;
              display: none !important;
            }
.slotHeaderClass {

}
.custom-slot-header-inner {
  padding: 0 !important;
  margin: 0 !important;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.resourceGroupHeaderInnerClass{
text-align:center;
width:100%;
}          
.resourceHeaderRowClass{
/*height:40px!important;*/
}
.resourceHeaderRowClass > * {
  height: 31px !important;
  box-sizing: border-box !important;
}     
          `}</style>
            <div
                id="calendar-debug-wrapper"
                style={{
                    minWidth: documentWidth < 500 ? "95vw" : "500px",
                    //height: `${totalHeight}px`,
                    overflow: "hidden",
                    width: "100%"
                }}
            ><>
                    {showOnlyBookings && filteredEquipments.length === 0 && (
                        <Box margin="xxl" padding="xxl" textAlign='center'>
                            <StatusIndicator type="not-started">
                                Sin reservas de acuerdo a los filtros seleccionados

                            </StatusIndicator>
                        </Box>
                    )}
                    {((showOnlyBookings && filteredEquipments.length > 0) || !showOnlyBookings) && (

                        <Calendar
                            height={"auto"}
                            initialView="resourceTimelineDay"
                            ref={calendarRef}
                            plugins={[interactionPlugin, themePlugin, resourceTimelinePlugin]}
                            events={allEvents}
                            resources={filteredEquipments}
                            headerToolbar={false}
                            initialDate={formattedDate}
                            firstDay={1}
                            slotMinTime="08:00:00"
                            slotMaxTime="22:00:00"
                            resourceColumnsWidth="90px"
                            resourceColumnHeaderContent="Horario"
                            locale='es'
                            resourceGroupField='groupId'
                            resourceIndentClass="hidden-resource-indent"
                            //resourceExpanderClass="hidden-resource-expander"
                            eventContent={(eventInfo) => renderEventContent(eventInfo, parameters.theme)}
                            tableHeaderSticky
                            slotMinWidth={50}
                            slotDuration="01:00:00"
                            slotHeaderInterval="01:00:00"
                            slotHeaderFormat={{
                                hour: 'numeric',
                                minute: '2-digit',
                                meridiem: 'short',
                                hour12: false
                            }}
                            //editable
                            selectable={onDateClick !== undefined}
                            resourcesInitiallyExpanded
                            dateClick={handleDateClickLocal}
                            resourceOrder="registration, equipment"
                            eventColor={"transparent"}
                            colorScheme={parameters.theme === "dark" ? "dark" : "light"}
                            slotHeaderClass='custom-slot-header slotHeaderClass'
                            slotHeaderInnerClass='custom-slot-header-inner slotHeaderInnerClass'

                            resourceGroupHeaderClass="resourceGroupHeaderClass"
                            resourceGroupHeaderInnerClass="resourceGroupHeaderInnerClass"
                            resourceHeaderRowClass="resourceHeaderRowClass"

                            resourceGroupLaneClass="resourceGroupLaneClass"
                            resourceGroupLaneInnerClass="resourceGroupLaneInnerClass"
                            timelineTopClass="timelineTopClass"
                            timelineBottomClass="timelineBottomClass"
                            slotHeaderContent={(args: any) => (
                                <div style={{ fontWeight: "bold", textAlign: "center" }}>&nbsp;&nbsp;{format(args.date, "HH:mm")}</div>
                            )}


                            resourceColumns={[
                                {
                                    //field: 'title',
                                    headerContent: 'Material',
                                    cellContent: (arg: any) => (
                                        <div style={{
                                            fontWeight: 'bold', height: 60,
                                            overflowWrap: "break-word",

                                            wordBreak: "break-word",
                                            whiteSpace: "pre-wrap"

                                        }}>
                                            {!!onResourceClick ?
                                                <Link onClick={() => { if (!!onResourceClick) onDateClick("", arg.resource.extendedProps) }}>{(arg.resource.title)}</Link>
                                                :
                                                <span>{(arg.resource.title)}</span>}

                                            {arg.resource.extendedProps.jsonOwners?.length > 0 ? (

                                                <span style={{ paddingLeft: 5 }}>
                                                    <Popover
                                                        renderWithPortal
                                                        header="Aeronave privada"
                                                        content={
                                                            <SpaceBetween direction='vertical' size="s">
                                                                <FormField label={"Propietarios:"}>
                                                                    <List
                                                                        items={arg.resource.extendedProps.jsonOwners.map((idUser: any) => {
                                                                            const user = getUser(idUser, data);
                                                                            const displayName = user?.name ?? user?.displayName ?? idUser;

                                                                            return {
                                                                                id: `usr${idUser}`,
                                                                                userName: displayName,
                                                                            };
                                                                        })}
                                                                        renderItem={(item: any) => ({
                                                                            id: item.id,
                                                                            content: `${item.userName}`,
                                                                        })}
                                                                    />
                                                                </FormField>
                                                            </SpaceBetween>
                                                        }
                                                    >
                                                        <Icon path={mdiTagHidden} size={0.8} />
                                                    </Popover>
                                                </span>
                                            ) : null}


                                            {/* Badge for Out of Flight */}
                                            {arg.resource.extendedProps.isOutOfFlight !== 0 && (
                                                <BadgeCustom size="small" color="red">
                                                    <Popover
                                                        header="Fuera de vuelo"
                                                        renderWithPortal
                                                        content={(() => {
                                                            const start = arg.resource.extendedProps.outOfFlightStart;
                                                            const end = arg.resource.extendedProps.outOfFlightEnd;
                                                            const startText = start ? <> desde el <strong>{start}</strong></> : "";
                                                            const endText = end ? <> hasta el <strong>{end}</strong></> : "";
                                                            return <>Material se encuentra fuera de vuelo{startText}{endText}</>
                                                        })()}
                                                    >
                                                        FDV
                                                    </Popover>
                                                </BadgeCustom>
                                            )}
                                        </div>
                                    )
                                }
                            ]}

                            eventClick={(info) => {
                                if (onBookingClick) {
                                    onBookingClick(info.event.extendedProps.booking as ItemBooking);
                                } else {
                                    setBooking(info.event.extendedProps.booking as ItemBooking);
                                }
                            }}
                        />
                    )}
                    {!!booking && !onBookingClick && (
                        <Booking
                            booking={booking}
                            idUser={idUser}
                            date={date}
                            onDelete={(booking: ItemBooking) => {
                                if (onBookingDelete) {
                                    onBookingDelete(booking);
                                }
                                setBooking(null)
                            }}
                            onDismiss={() => setBooking(null)}
                        />
                    )}
                    {!!info && (
                        <Confirm
                            type="info"
                            onDismiss={() => setInfo(null)} show={!!info} action={'Reservar'}
                            warning={`Ir a reservar el ${info?.registration} el ${info?.date} desde las ${info?.startTime}:00`}
                            onConfirm={() => {
                                window.location.hash = `#members/bookings/book?date=${info?.date}&idGroupType=${info?.idGroupType}&idEquipment=${info?.idEquipment}&startTime=${info?.startTime}`;
                            }}
                        />
                    )}
                </>
            </div>
        </>
    )
};


export default BookingGrid;



function renderEventContent(eventInfo: any, theme: string) {

    const booking = eventInfo.event.extendedProps.booking as ItemBooking;
    const data = eventInfo.event.extendedProps.booking as any;
    const towType = {
        MOUNTAIN: <BadgeCustom color="lime">C</BadgeCustom>,
        SANRAMON: <BadgeCustom color="violet">SR</BadgeCustom>,
        LOCAL: <BadgeCustom color="brown">LC</BadgeCustom>,
        NONE: <BadgeCustom color="grey">AD</BadgeCustom>,
        BALLAST: <BadgeCustom color="violet">LA</BadgeCustom>
    }
    return (
        eventInfo.event.extendedProps.type == "free" ? (
            <div style={{
                border: "green",
                backgroundColor: "green",
                borderRadius: "4px",
                padding: "1px 2px 1px 5px",
                height: '18px',
                width: '100%',
                textAlign: 'center'
            }}>
                <small>Reservar</small>
            </div>
        ) :

            eventInfo.event.extendedProps.type == "lock" ? (
                <div style={{
                    border: "orange",
                    backgroundColor: "orange",
                    borderRadius: "4px",
                    padding: "1px 2px 1px 5px",
                    height: '18px',
                    width: '100%',
                    textAlign: 'center'
                }}>
                    <Popover renderWithPortal header="Bloqueado" content={eventInfo.event.title}>Bloqueado</Popover>
                </div>) :
                eventInfo.event.extendedProps.type == "oof" ? (

                    <div style={{
                        border: "red",
                        backgroundColor: "red",
                        borderRadius: "4px",
                        height: '18px',
                        width: '100%',
                        textAlign: 'center'
                    }}>
                        <Popover
                            header="Fuera de vuelo"
                            renderWithPortal
                            content={(() => {
                                const start = eventInfo.event.extendedProps.outOfFlightStart;
                                const end = eventInfo.event.extendedProps.outOfFlightEnd;
                                const startText = start ? <> desde el <strong>{start}</strong></> : "";
                                const endText = end ? <> hasta el <strong>{end}</strong></> : "";
                                return <>Material se encuentra fuera de vuelo{startText}{endText}</>
                            })()}
                        >
                            Fuera de vuelo
                        </Popover>

                    </div>
                ) : eventInfo.event.extendedProps.type == "booking" ? (
                    <div style={{
                        border: theme === "dark" ? "1px solid #444" : "1px solid #eaeaea",
                        backgroundColor: theme === "dark" ? "#333" : "#eaeaea",
                        borderRadius: "4px",
                        padding: "2px",
                        maxHeight: '60px',
                        width: '100%',
                        overflow: 'hidden',
                        boxSizing: 'border-box',
                        cursor: 'pointer'
                    }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: theme === "dark" ? "white" : "black" }}>
                            <strong>{booking.displayName}</strong>
                        </div>

                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            minWidth: 0, // 🔑 Essential for flex child truncation/clipping
                            color: theme === "dark" ? "white" : "black"
                        }}>
                            {
                                data.instructor && data.instructor !== '' ? (
                                    <Icon path={mdiHumanMaleBoard} size={0.8} />
                                ) : null}
                            {data.equipmentType === "GLIDER" && (
                                <span key={`DV${data.idBooking}`}>{towType[data.towType as keyof typeof towType]}</span>
                            )}
                            <BadgeCustom key={`BT${data.idBooking}`} color={data.bookingTypeBadge}>{data.bookingType.substring(0, 1)}</BadgeCustom>


                            {data.obsBooking && data.obsBooking !== '' ? (
                                <BadgeCustom color="grey" key={`OBS${data.idBooking}`}>
                                    <Icon path={mdiInformation} size={0.75} />
                                </BadgeCustom>

                            ) : null}
                            {data.pax > 0 ? <BadgeCustom color="grey" key={`OBS${data.idBooking}`}><Icon path={mdiAccountMultiple} size={0.6} />X{data.pax}</BadgeCustom> : null}
                        </div>
                    </div>

                ) : null

    );
}

function useDocumentWidth() {
    const [width, setWidth] = useState<number>(0);

    useEffect(() => {
        const updateWidth = () => {
            // clientWidth excludes scrollbars (most common choice for document layout)
            const docWidth = document.documentElement.clientWidth || document.body.clientWidth;
            setWidth(docWidth);
        };

        // Set initial width
        updateWidth();

        // Listen for window resize
        window.addEventListener('resize', updateWidth);

        // Clean up event listener on unmount
        return () => window.removeEventListener('resize', updateWidth);
    }, []);

    return width;
}

function addBreaks(text: string, minChunk = 7) {
    if (!text) return "";

    let result = "";
    let i = 0;

    while (i < text.length) {
        // If remaining characters are less than minChunk, append the rest and break
        if (i + minChunk >= text.length) {
            result += text.slice(i);
            break;
        }

        // Search for the first space after minChunk characters
        const nextSpaceIndex = text.indexOf(" ", i + minChunk);

        if (nextSpaceIndex === -1) {
            // No more spaces found, append remaining text
            result += text.slice(i);
            break;
        }

        // Append chunk up to space + <br />
        result += text.slice(i, nextSpaceIndex) + "<br />";

        // Move pointer past the space
        i = nextSpaceIndex + 1;
    }

    return result;
}
